import {
  CurriculumActivityProgress,
  CurriculumLevelProgress,
  CurriculumLevelProgressSummary,
  CurriculumMustKnowProgressSummary,
  TopicProgressResponse,
} from '~/curriculum/types/curriculum.types'
import { MindBlocks, MustKnowsLevelView, isFullLevelContent } from '~/curriculum/types/mapping-quiz.types'

/** Stable key for a must-know block; matches progress lookup by level slug when block slug is absent. */
export function getMustKnowBlockKey(block: MindBlocks): string {
  return block.slug ?? block.levels[0]?.slug ?? ''
}

/**
 * Drops repeated must-know blocks, keeping the first of each slug.
 *
 * hom-api stores one block per mindblock and never merges by title, so the same must-know can come
 * back once per mindblock group it appears in (`mustknows.utils.ts:buildMustKnowsSlim`). A slug-less
 * legacy block is always kept — it has no identity to dedupe on.
 */
export function dedupeMustKnowBlocksBySlug(mustKnowsData: MindBlocks[] | undefined): MindBlocks[] {
  const seenSlugs = new Set<string>()
  return (mustKnowsData ?? []).filter((block) => {
    if (!block.slug) return true
    if (seenSlugs.has(block.slug)) return false
    seenSlugs.add(block.slug)
    return true
  })
}

/**
 * Must-know tallies for a badge/card, matching hom-api's own definition
 * (`mustknow.helper.ts:getMustKnowProgress`): a must-know is a titled *block* of levels, and it is
 * complete only when every level in it is complete.
 *
 * `total` must come from the blocks (the mapping-quiz task row), never from the progress summary's
 * `mustKnows` map — that map only holds levels the user already has activity on, and its per-entry
 * `isComplete` is hard-coded `false` by the API and never updated.
 */
export function countMustKnowBlocks(
  mustKnowsData: MindBlocks[] | undefined,
  mustKnowProgress?: CurriculumMustKnowProgressSummary,
): { total: number; completed: number } {
  const blocks = dedupeMustKnowBlocksBySlug(mustKnowsData)
  return {
    total: blocks.length,
    completed: getMustKnowCompletionStatuses(blocks, mustKnowProgress).filter(Boolean).length,
  }
}

export function getCompletedMustKnowKeys(
  mustKnowsData: MindBlocks[] | undefined,
  mustKnowProgress?: CurriculumMustKnowProgressSummary,
): string[] {
  const statuses = getMustKnowCompletionStatuses(mustKnowsData, mustKnowProgress)
  const keys = mustKnowsData?.map((block, i) => (statuses[i] ? getMustKnowBlockKey(block) : null)) ?? []
  return [...new Set(keys.filter((k): k is string => Boolean(k)))]
}

/**
 * One level's progress, gathered from everywhere the summary files it.
 *
 * The summary's outer key is the must-know LEVEL's slug as it stood when the write happened
 * (`mustKnowSubtopic`, falling back to `'General'`), and the inner key is the level id. Two things
 * make the outer key unreliable to read by:
 *  - The whole-topic payload need not carry the slug at all. hom-api serves the stored slim blob
 *    as-is for a topic-wide `quiz-task` request and backfills a missing slug from the content doc
 *    ONLY when the request is scoped to a sub-topic (`getLeanMustKnowsView`), while the slim
 *    migration defaults a legacy level's slug to `''` (heavy Sanity docs stored it as an object).
 *    So the same level arrives with its slug on the sub-topic page and with `''` on the topic page
 *    — which is what made a finished must-know read as untouched there.
 *  - One level can be filed under more than one key across a topic's sub-topics (its own slug in
 *    one place, `'General'` in another where the slug was missing), and those copies can disagree.
 *    `mergeMustKnowProgressSummaries` only ORs entries that share an outer key, so the disagreement
 *    survives the merge.
 *
 * So this keys on `levelId` — the curriculumContent doc id, present on every payload — and folds
 * every copy it finds with `mergeLevelProgress`, which ORs completion and merges the activity
 * records. Taking the first copy instead could pick the stale one; a union cannot. A must-know with
 * several levels is unaffected by the change: each level is still resolved on its own id, so
 * `getMustKnowCompletionStatuses` still needs every one of them done before the block counts.
 */
function findLevelProgress(
  lvl: MustKnowsLevelView,
  mustKnowProgress: CurriculumMustKnowProgressSummary | undefined,
): CurriculumLevelProgress | undefined {
  if (!mustKnowProgress || !lvl.levelId) return undefined

  const filed = Object.values(mustKnowProgress)
    .map((mustKnow) => mustKnow?.levels?.[lvl.levelId])
    .filter((progress): progress is CurriculumLevelProgress => Boolean(progress))
    // `activity` is optional in practice (the full-content path guards it too), and merging reads
    // its keys — so normalise before folding.
    .map((progress) => ({ isComplete: progress.isComplete, activity: progress.activity ?? {} }))

  return filed.length ? filed.reduce(mergeLevelProgress) : undefined
}

/**
 * True when a must-know level is complete in the user's progress.
 *
 * Progress is found by `findLevelProgress` above: the level's slug first, then its level id.
 * Two shapes reach this function:
 *  - Lightweight (topic overview): no inlined `content`, so we cannot recompute from materials —
 *    trust the aggregate `progress.isComplete`, which the API derives from the level's progressIds.
 *  - Full (sub-topic slice): `content` is inlined, so we recompute from learning materials and
 *    activities (difficulties must all be completed when present) to match MasteryTrackMenu.
 */
export function isMustKnowLevelCompleteFromProgress(
  lvl: MustKnowsLevelView,
  mustKnowProgress: CurriculumMustKnowProgressSummary | undefined,
): boolean {
  const progress = findLevelProgress(lvl, mustKnowProgress)

  if (!progress) return false

  // Lightweight level (no inlined content): fall back to the aggregate completion flag.
  if (!isFullLevelContent(lvl)) return progress.isComplete

  const { content } = lvl
  const activitiesProgress = progress.activity || {}

  const allLearningMaterialsCompleted = (content.learningMaterials || []).every((material) => {
    const activityProgress = activitiesProgress[material._id]
    return activityProgress?.isComplete === true
  })

  const allActivitiesCompleted = (content.activities || []).every((activity) => {
    const activityProgress = activitiesProgress[activity._id]

    if (!activityProgress?.isComplete) return false

    if (activityProgress.difficulties) {
      return Object.values(activityProgress.difficulties).every((diff) => diff.isCompleted === true)
    }

    return true
  })

  return allLearningMaterialsCompleted && allActivitiesCompleted
}

export function getMustKnowCompletionStatuses(
  mustKnowsData: MindBlocks[] | undefined,
  mustKnowProgress?: CurriculumMustKnowProgressSummary,
): boolean[] {
  return (
    mustKnowsData?.map((mustKnowBlock) =>
      mustKnowBlock?.levels?.every((lvl) => isMustKnowLevelCompleteFromProgress(lvl, mustKnowProgress)),
    ) ?? []
  )
}

type MustKnowEntry = CurriculumMustKnowProgressSummary[string]

function mergeActivityProgress(
  a: CurriculumActivityProgress,
  b: CurriculumActivityProgress,
): CurriculumActivityProgress {
  return {
    ...a,
    ...b,
    type: a.type || b.type,
    isComplete: a.isComplete || b.isComplete,
    isLocked: a.isLocked || b.isLocked,
    difficulties: mergeActivityDifficulties(a.difficulties, b.difficulties),
  }
}

function mergeActivityDifficulties(
  a: CurriculumActivityProgress['difficulties'],
  b: CurriculumActivityProgress['difficulties'],
): CurriculumActivityProgress['difficulties'] {
  if (!a && !b) return undefined
  if (!a) return b
  if (!b) return a
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  return Array.from(keys).reduce<NonNullable<typeof a>>((out, k) => {
    const da = a[k]
    const db = b[k]
    if (da && db) {
      out[k] = {
        ...da,
        ...db,
        isCompleted: da.isCompleted || db.isCompleted,
        accuracy: Math.max(da.accuracy, db.accuracy),
      }
    } else {
      out[k] = da ?? db!
    }
    return out
  }, {})
}

function mergeActivityRecord(
  a: CurriculumLevelProgress['activity'],
  b: CurriculumLevelProgress['activity'],
): CurriculumLevelProgress['activity'] {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  return Array.from(keys).reduce<CurriculumLevelProgress['activity']>((out, id) => {
    const actA = a[id]
    const actB = b[id]
    if (actA && actB) out[id] = mergeActivityProgress(actA, actB)
    else out[id] = actA ?? actB!
    return out
  }, {})
}

function mergeLevelProgress(a: CurriculumLevelProgress, b: CurriculumLevelProgress): CurriculumLevelProgress {
  return {
    isComplete: a.isComplete || b.isComplete,
    activity: mergeActivityRecord(a.activity, b.activity),
  }
}

function mergeLevelProgressSummary(
  a: CurriculumLevelProgressSummary,
  b: CurriculumLevelProgressSummary,
): CurriculumLevelProgressSummary {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  return Array.from(keys).reduce<CurriculumLevelProgressSummary>((out, id) => {
    const la = a[id]
    const lb = b[id]
    if (la && lb) out[id] = mergeLevelProgress(la, lb)
    else out[id] = la ?? lb!
    return out
  }, {})
}

function mergeMustKnowEntry(a: MustKnowEntry, b: MustKnowEntry): MustKnowEntry {
  return {
    isComplete: a.isComplete || b.isComplete,
    topic: a.topic || b.topic,
    levels: mergeLevelProgressSummary(a.levels, b.levels),
    summary: mergeLevelProgressSummary(a.summary, b.summary),
  }
}

function mergeMustKnowProgressSummaries(
  merged: CurriculumMustKnowProgressSummary,
  next: CurriculumMustKnowProgressSummary,
): CurriculumMustKnowProgressSummary {
  const keys = new Set([...Object.keys(merged), ...Object.keys(next)])
  return Array.from(keys).reduce<CurriculumMustKnowProgressSummary>((out, slug) => {
    const a = merged[slug]
    const b = next[slug]
    if (a && b) out[slug] = mergeMustKnowEntry(a, b)
    else out[slug] = a ?? b!
    return out
  }, {})
}

export function mergeTopicMustKnowProgress(
  topicProgressSummary: TopicProgressResponse | undefined,
  topicId: string | undefined,
): CurriculumMustKnowProgressSummary {
  if (!topicProgressSummary || !topicId) return {}
  const entry = topicProgressSummary.topics[topicId]
  if (!entry?.subtopics) return {}
  return Object.values(entry.subtopics).reduce<CurriculumMustKnowProgressSummary>((merged, subtopic) => {
    return mergeMustKnowProgressSummaries(merged, subtopic.mustKnows ?? {})
  }, {})
}
